import React from "react";
import {
  Cloud,
  CloudRain,
  CloudSun,
  Clock,
  Moon,
  Sun,
  Sunrise,
  Sunset,
  Thermometer,
  ThermometerSun,
  Waves,
  Wind,
} from "lucide-react";

function getWeatherConditionIcon(condition) {
  const normalized = String(condition || "").toLowerCase();

  if (normalized.includes("rain") || normalized.includes("showers")) {
    return CloudRain;
  }

  if (normalized.includes("cloud")) {
    return normalized.includes("part") ? CloudSun : Cloud;
  }

  if (normalized.includes("sun") || normalized.includes("clear")) {
    return Sun;
  }

  return ThermometerSun;
}

function getWeatherDisplayParts(value, condition) {
  const rawValue = String(value || "").trim();
  const rawCondition = String(condition || "").trim();

  if (!rawValue) {
    return { condition: rawCondition, temperature: "" };
  }

  const match = rawValue.match(
    /^(.+?),\s*(-?\d+(?:\.\d+)?\s*(?:\u00b0|\u00c2\u00b0)?\s*[CF])$/i
  );
  if (!match) {
    return {
      condition: rawCondition || rawValue,
      temperature: "",
    };
  }

  return {
    condition: match[1].trim(),
    temperature: match[2].replace(/\s+/g, " ").trim(),
  };
}

export default function InfoPopupConditionsSection({ infoPopup }) {
  const {
    t,
    isHistoricalContextLoading,
    weatherValue,
    weatherCondition,
    hasStructuredWind,
    windTextFromObject,
    windArrow,
    windValue,
    waterTempValue,
    moonValue,
    sunriseLocal,
    sunriseUtc,
    sunsetLocal,
    sunsetUtc,
    sunRelation,
    tideType,
    tideHeight,
  } = infoPopup;

  const windDisplayValue = hasStructuredWind ? (
    <span className="info-popup-condition-value-inline">
      {windTextFromObject}
      {windTextFromObject && windArrow ? " " : null}
      {windArrow}
    </span>
  ) : (
    windValue
  );

  const sunriseValue = sunriseLocal ? (
    <>
      {sunriseLocal} {t("local_time")}
      {sunriseUtc && (
        <>
          {" ("}
          {sunriseUtc} {t("utc_time")}
          {")"}
        </>
      )}
    </>
  ) : null;

  const sunsetValue = sunsetLocal ? (
    <>
      {sunsetLocal} {t("local_time")}
      {sunsetUtc && (
        <>
          {" ("}
          {sunsetUtc} {t("utc_time")}
          {")"}
        </>
      )}
    </>
  ) : null;

  const tideValue = tideType || tideHeight !== null ? (
    <>
      {tideType}
      {tideHeight !== null && (
        <>{tideType ? ` (${tideHeight} m)` : `${tideHeight} m`}</>
      )}
    </>
  ) : null;

  const weatherDisplay = getWeatherDisplayParts(weatherValue, weatherCondition);
  const weatherCardValue = weatherDisplay.temperature ? (
    <span className="info-popup-weather-value">
      <span>{weatherDisplay.condition}</span>
      <span>{weatherDisplay.temperature}</span>
    </span>
  ) : (
    weatherDisplay.condition
  );

  const hasConditionCards =
    Boolean(weatherValue) ||
    Boolean(windDisplayValue) ||
    Boolean(waterTempValue) ||
    Boolean(moonValue) ||
    Boolean(sunriseValue) ||
    Boolean(sunsetValue) ||
    Boolean(sunRelation) ||
    Boolean(tideValue);

  const renderConditionCard = (Icon, label, value) => (
    <div className="info-popup-detail-card info-popup-condition-card">
      <span className="info-popup-detail-card-icon" aria-hidden="true">
        <Icon size={16} strokeWidth={2.25} />
      </span>
      <span className="info-popup-detail-card-copy">
        <span className="info-popup-detail-card-label">{label}</span>
        <strong>{value}</strong>
      </span>
    </div>
  );

  const renderSkeletonCards = () =>
    Array.from({ length: 8 }).map((_, index) => (
      <div
        className="info-popup-detail-card info-popup-condition-card info-popup-condition-card--skeleton"
        key={index}
        aria-hidden="true"
      >
        <span className="info-popup-condition-skeleton-icon" />
        <span className="info-popup-condition-skeleton-copy">
          <span className="info-popup-condition-skeleton-line info-popup-condition-skeleton-line--label" />
          <span className="info-popup-condition-skeleton-line info-popup-condition-skeleton-line--value" />
        </span>
      </div>
    ));

  if (!isHistoricalContextLoading && !hasConditionCards) {
    return null;
  }

  return (
    <section className="info-popup-section info-popup-section--conditions">
      <h4 className="info-popup-section-title">
        {t("info_popup_section_weather_conditions")}
      </h4>

      {isHistoricalContextLoading ? (
        <div
          className="info-popup-detail-grid info-popup-condition-grid info-popup-condition-grid--loading"
          role="status"
          aria-label={t("info_popup_loading_weather_conditions")}
        >
          {renderSkeletonCards()}
        </div>
      ) : (
        <div className="info-popup-detail-grid info-popup-condition-grid">
          {weatherValue &&
            renderConditionCard(
              getWeatherConditionIcon(weatherCondition || weatherValue),
              t("weather"),
              weatherCardValue
            )}

          {windDisplayValue &&
            renderConditionCard(Wind, t("wind"), windDisplayValue)}

          {waterTempValue &&
            renderConditionCard(Thermometer, t("water_temp_sst"), waterTempValue)}

          {moonValue && renderConditionCard(Moon, t("moon_phase"), moonValue)}

          {sunriseValue &&
            renderConditionCard(Sunrise, t("sunrise"), sunriseValue)}

          {sunsetValue &&
            renderConditionCard(Sunset, t("sunset"), sunsetValue)}

          {sunRelation &&
            renderConditionCard(Clock, t("relative_to_sun"), sunRelation)}

          {tideValue && renderConditionCard(Waves, t("tide"), tideValue)}
        </div>
      )}
    </section>
  );
}
